// @ts-strict-ignore
import List from 'antd/lib/list';
import Collapse from 'antd/lib/collapse';
import * as React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
    selectAppPlatform,
    selectAppStoreType,
    selectDestinationInfoByDestinationStepConfigId,
    selectNotificationIntegration,
    selectNotifications,
    selectOrgProvidersById,
    selectStepConfigDataFromStepGraphs,
    selectWorkflowId,
} from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import { AppComponentProps } from '../../typescriptHelpers';
import { Destination } from '@/types/destinations';
import styles from '../styles/settingsStyles';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import NotificationSettingsModal from './NotificationSettingsModal';
import NotificationStepConfigOverrideRow from './NotificationStepConfigOverrideRow';
import AddStepOverrideModal from './AddStepOverrideModal';
import Search from 'antd/lib/input/Search';
import NotificationRow from '../common/NotificationRow';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { createUserSettingsRoute } from '@/helpers/routeHelpers';
import { UserSettingsPageView } from '@/components/userSettings/UserSettingsComponent';
import { SortTableButton } from '@/components/tableFilters/SortTableButton';
import { SettingsSortType } from '@/types/sorts';
import { AppNotificationType, CommonNotification, StepNotificationConfigOverride } from '../../types';
import { CaretRightOutlined, ControlOutlined, SortAscendingOutlined } from '@ant-design/icons';
import { antGrey, runwayColors } from '../styles/globalStyles';
import {
    tryUpdateAppSettings,
    tryUpdateAppSettingsV2,
    UpdateNotificationSettingsRequest,
    UpdateStepNotificationConfigRequest,
} from '../../actions/appSettings';
import { AppSettingsUpdatable, AppSettingsV2Updatable } from '../../actions/updatable';

const CARET_SLOT_WIDTH = 24;

interface AppNotificationsProps extends AppComponentProps {
    refProp?: React.MutableRefObject<HTMLDivElement>;
}

const AppNotifications: React.FC<AppNotificationsProps> = (props: AppNotificationsProps) => {
    const { appId, orgId } = props;
    const dispatch = useDispatch();
    const notifications = useSelector((state: RootState) => selectNotifications(state, appId));
    const stepGraphStepsMap = useSelector((state: RootState) => selectStepConfigDataFromStepGraphs(state, appId));
    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, appId));
    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const destinationInfoByDestinationStepConfigId = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, appId),
    );

    const destinationsByStepConfigId = React.useMemo<Record<string, Destination[]>>(() => {
        if (!stepGraphStepsMap) return {};
        const result: Record<string, Destination[]> = {};
        for (const [stepConfigId, step] of Object.entries(stepGraphStepsMap)) {
            result[stepConfigId] = step.destinationStepConfigIds
                .map((id) => destinationInfoByDestinationStepConfigId[id])
                .filter((d): d is Destination => d !== undefined);
        }
        return result;
    }, [stepGraphStepsMap, destinationInfoByDestinationStepConfigId]);

    // Resolves channel id → name for the tooltips (channel/mention).
    const availableChannelsById = useSelector((state: RootState) => {
        const integration = selectNotificationIntegration(state, appId);
        if (!integration) return {} as Record<string, string>;
        const providersById = selectOrgProvidersById(state, orgId);
        const provider = providersById[integration.providerId];
        const channels = provider?.data?.availableChannels ?? [];
        const map: Record<string, string> = {};
        for (const c of channels as Array<{ id: string; name: string }>) {
            map[c.id] = c.name;
        }
        return map;
    });

    const primaryChannelNames = useSelector((state: RootState) => {
        const integration = selectNotificationIntegration(state, appId);
        const data = integration?.data as { primaryChannels?: Array<{ name: string }> } | undefined;
        return (data?.primaryChannels ?? []).map((c) => c.name);
    });

    const [showNotificationSettings, setShowNotificationSettings] = React.useState(false);
    const [notificationIdSelectedForSettings, setNotificationIdSelectedForSettings] = React.useState(null);
    const [searchValue, setSearchValue] = React.useState('');
    const [selectedSortType, setSelectedSortType] = React.useState<SettingsSortType>(SettingsSortType.Name);
    const [addOverrideForNotificationId, setAddOverrideForNotificationId] = React.useState<AppNotificationType | null>(
        null,
    );

    // State for step override modal
    const [stepOverrideModal, setStepOverrideModal] = React.useState<{
        notificationId: AppNotificationType;
        stepConfigId: string;
        stepConfigName: string;
        override: StepNotificationConfigOverride | null;
    } | null>(null);

    const history = useHistory();
    const location = useLocation();

    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const idFromQuery = params.get('notificationId');
        if (idFromQuery) {
            const stepConfigIdFromQuery = params.get('stepConfigId');
            if (stepConfigIdFromQuery && stepGraphStepsMap) {
                // Deep link to a step override modal, but only if that step already has an override —
                // without one the app-level setting is what governed the notification.
                const stepGraphStep = stepGraphStepsMap[stepConfigIdFromQuery];
                const notification = notifications.find((n) => n.id === idFromQuery);
                const override =
                    notification?.stepConfigOverrides?.find((o) => o.stepConfigId === stepConfigIdFromQuery) ?? null;
                if (stepGraphStep && override) {
                    setStepOverrideModal({
                        notificationId: idFromQuery as AppNotificationType,
                        stepConfigId: stepConfigIdFromQuery,
                        stepConfigName: stepGraphStep.stepName,
                        override,
                    });
                    history.replace({ search: null });
                    return;
                }
            }
            // Default: open app-level notification settings
            setNotificationIdSelectedForSettings(idFromQuery);
            setShowNotificationSettings(true);
            history.replace({ search: null });
        }
    }, [location.search, stepGraphStepsMap, notifications]);

    // Single source of truth for optimistic toggle state.
    const [optimistic, setOptimistic] = React.useState<Record<string, boolean>>({});

    // Tracks if we've seen isLoading=true for each key (avoids clearing before saga starts).
    const sawLoadingRef = React.useRef<Record<string, boolean>>({});

    // Keys of in-flight notification update requests (covers both app-level and per-step-override endpoints).
    const loadingKeys = useSelector(
        (state: RootState) =>
            state.ui.loading
                .filter(
                    (l) =>
                        l.actionName === AppSettingsUpdatable.UpdateNotificationSetting ||
                        l.actionName === AppSettingsV2Updatable.UpdateStepNotificationConfig,
                )
                .map((l) => l.id as string),
        shallowEqual,
    );

    // Clear an optimistic entry once its dispatch has completed (loading went true → false).
    React.useEffect(() => {
        const loadingSet = new Set(loadingKeys);
        setOptimistic((prev) => {
            const keys = Object.keys(prev);
            if (keys.length === 0) return prev;
            const next = { ...prev };
            let changed = false;
            for (const key of keys) {
                if (loadingSet.has(key)) {
                    sawLoadingRef.current[key] = true;
                } else if (sawLoadingRef.current[key]) {
                    delete next[key];
                    delete sawLoadingRef.current[key];
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [loadingKeys]);

    const handleToggle = (
        notification: CommonNotification,
        clickedOverride: StepNotificationConfigOverride | null,
        newValue: boolean,
    ) => {
        const overrides = notification.stepConfigOverrides ?? [];

        const childTargetState: Record<string, boolean> = {};
        for (const o of overrides) {
            if (clickedOverride == null || o.stepConfigId === clickedOverride.stepConfigId) {
                childTargetState[o.stepConfigId] = newValue;
            } else {
                childTargetState[o.stepConfigId] = o.enabled;
            }
        }

        const allChildrenMatchNewValue = overrides.every((o) => childTargetState[o.stepConfigId] === newValue);
        const parentTarget: boolean | null =
            clickedOverride == null ? newValue
            : allChildrenMatchNewValue ? newValue
            : null;

        setOptimistic((prev) => {
            const next = { ...prev };
            if (parentTarget != null) next[notification.id] = parentTarget;
            for (const o of overrides) {
                const key = `${notification.id}#${o.stepConfigId}`;
                if (
                    childTargetState[o.stepConfigId] !== o.enabled ||
                    o.stepConfigId === clickedOverride?.stepConfigId
                ) {
                    next[key] = childTargetState[o.stepConfigId];
                }
            }
            return next;
        });

        if (parentTarget != null) {
            const request: UpdateNotificationSettingsRequest = {
                orgId,
                appId,
                notificationId: notification.id,
                enabled: parentTarget,
                channels: notification.channels?.map((c) => c.id) ?? [],
                mentionUsers: notification.mentionUsers ?? [],
                mentionGroups: notification.mentionGroups ?? [],
                hourOfDayForNotification: notification.hourOfDayForNotification,
                appleProfileAndCertificateReminderDaysBeforeExpiration:
                    notification.appleProfileAndCertificateReminderDaysBeforeExpiration,
                selectedProfileAndCertificateTypes: notification.selectedProfileAndCertificateTypes,
                includeNewIssuesIdentifiedByRunway: notification.includeNewIssuesIdentifiedByRunway,
                includeThreadRelatedNotifications: notification.threadWithRelatedNotifications,
                emailsToInclude: notification.emailsToInclude,
                notificationChannels: notification.notificationChannels,
                customCopy: notification.customCopy,
                includeTicketListInSubmitNotifications: notification.includeTicketListInSubmitNotifications,
                bundleSizeIncreaseConditions: notification.bundleSizeIncreaseConditions,
                bundleSizeCellularDownloadLimitThresholdMB: notification.bundleSizeCellularDownloadLimitThresholdMB,
                bundleSizeAlertNotifyFor: notification.bundleSizeAlertNotifyFor,
                includeDirectDownloadLink: notification.includeDirectDownloadLink,
                directDownloadLinkAccessLimit: notification.directDownloadLinkAccessLimit,
                directDownloadLinkExpiryHours: notification.directDownloadLinkExpiryHours,
                includePROwnerInCherryPickNotifications: notification.includePROwnerInCherryPickNotifications,
                includePRCommitInfo: notification.includePRCommitInfo,
                mentionReleasePilot: notification.mentionReleasePilot,
                conditionGroups: notification.conditionGroups,
            };
            dispatch(tryUpdateAppSettings(AppSettingsUpdatable.UpdateNotificationSetting, request, notification.id));
        }

        if (workflowId) {
            for (const o of overrides) {
                if (childTargetState[o.stepConfigId] === o.enabled) continue;
                const key = `${notification.id}#${o.stepConfigId}`;
                const req: UpdateStepNotificationConfigRequest = {
                    orgId,
                    appId,
                    workflowId,
                    stepConfigId: o.stepConfigId,
                    notificationId: notification.id,
                    enabled: childTargetState[o.stepConfigId],
                };
                dispatch(tryUpdateAppSettingsV2(AppSettingsV2Updatable.UpdateStepNotificationConfig, req, key));
            }
        }
    };

    const computeSwitchState = (notification: CommonNotification): 'on' | 'off' | 'indeterminate' => {
        const parentEnabled = optimistic[notification.id] ?? notification.enabled;
        const overrides = notification.stepConfigOverrides ?? [];
        if (overrides.length === 0) return parentEnabled ? 'on' : 'off';
        const childStates = overrides.map((o) => optimistic[`${notification.id}#${o.stepConfigId}`] ?? o.enabled);
        const all = [parentEnabled, ...childStates];
        if (all.every((s) => s === true)) return 'on';
        if (all.every((s) => s === false)) return 'off';
        return 'indeterminate';
    };

    const processedNotifications = React.useMemo(() => {
        const filtered = notifications.filter((notif) =>
            notif.displayText.toLowerCase().includes(searchValue.toLowerCase()),
        );

        return [...filtered].sort((a, b) => {
            switch (selectedSortType) {
                case SettingsSortType.Name:
                    return a.displayText.localeCompare(b.displayText);

                case SettingsSortType.EnabledFirst:
                    if (a.enabled === b.enabled) return a.displayText.localeCompare(b.displayText);
                    return a.enabled ? -1 : 1;

                default:
                    return 0;
            }
        });
    }, [notifications, searchValue, selectedSortType]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} ref={props.refProp}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Space size={8}>
                    <Text style={styles.appSettingsSectionTitleStyle}>Notifications</Text>
                </Space>
                <Space>
                    <SortTableButton<SettingsSortType>
                        selectedSortType={selectedSortType}
                        sortOptions={[
                            {
                                sortType: SettingsSortType.Name,
                                text: 'Name',
                                icon: <SortAscendingOutlined style={{ color: runwayColors.primary }} />,
                            },
                            {
                                sortType: SettingsSortType.EnabledFirst,
                                text: 'Enabled',
                                icon: <ControlOutlined style={{ color: runwayColors.primary }} />,
                            },
                        ]}
                        onSelectSort={(sortType) => {
                            setSelectedSortType(sortType);
                        }}
                    />

                    <Search
                        placeholder="Search notifications"
                        allowClear
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onSearch={(value) => {
                            setSearchValue(value);
                        }}
                        style={{ width: 400 }}
                    />
                </Space>
            </div>
            <List
                header={null}
                pagination={{ hideOnSinglePage: true, defaultPageSize: 10, size: 'small', showSizeChanger: false }}
                dataSource={processedNotifications}
                renderItem={(notification) => {
                    const overrides = notification.stepConfigOverrides ?? [];
                    const childOverrides =
                        stepGraphStepsMap ?
                            overrides
                                .map((o) => ({ step: stepGraphStepsMap[o.stepConfigId], override: o }))
                                .filter((p) => p.step != null)
                        :   [];
                    const hasChildren = childOverrides.length > 0;
                    const loadingSet = new Set(loadingKeys);
                    const parentIsUpdating =
                        loadingSet.has(notification.id) ||
                        overrides.some((o) => loadingSet.has(`${notification.id}#${o.stepConfigId}`));

                    const notificationRow = (
                        <div style={{ paddingBlock: 12, flex: 1 }}>
                            <NotificationRow
                                notification={notification}
                                notificationsEnabled
                                switchState={computeSwitchState(notification)}
                                isUpdating={parentIsUpdating}
                                onClickSettings={(notificationId) => {
                                    setNotificationIdSelectedForSettings(notificationId);
                                    setShowNotificationSettings(true);
                                }}
                                onToggleEnabled={(enabled) => handleToggle(notification, null, enabled)}
                                onAddOverride={(notificationId) =>
                                    setAddOverrideForNotificationId(notificationId as AppNotificationType)
                                }
                                actionType={RunwayUserAction.UAUpdateAppSettings}
                                stepGraphStepsMap={stepGraphStepsMap}
                                destinationsByStepConfigId={destinationsByStepConfigId}
                                platform={platform}
                                appStoreType={appStoreType}
                                availableChannelsById={availableChannelsById}
                                primaryChannelNames={primaryChannelNames}
                            />
                        </div>
                    );

                    if (!hasChildren) {
                        // Reserve the caret slot so rows with and without children line up
                        return (
                            <List.Item
                                key={notification.id}
                                style={{
                                    paddingBlock: 0,
                                    paddingInline: 16,
                                    marginInline: -16,
                                    borderBlockEnd: `1px solid ${antGrey.grey5}`,
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                    <div style={{ width: CARET_SLOT_WIDTH, flexShrink: 0 }} />
                                    {notificationRow}
                                </div>
                            </List.Item>
                        );
                    }

                    return (
                        <List.Item
                            key={notification.id}
                            style={{
                                paddingBlock: 0,
                                paddingInline: 16,
                                marginInline: -16,
                                borderBlockEnd: `1px solid ${antGrey.grey5}`,
                            }}
                        >
                            <Collapse
                                className="automations-collapsible notifications-collapsible"
                                defaultActiveKey={[]}
                                style={{ flex: 1 }}
                                ghost
                                expandIcon={({ isActive }) => (
                                    <CaretRightOutlined
                                        style={{
                                            color: antGrey.grey6,
                                            fontSize: 14,
                                        }}
                                        rotate={isActive ? 90 : 0}
                                    />
                                )}
                            >
                                <Collapse.Panel header={notificationRow} key="1">
                                    <List
                                        size="small"
                                        dataSource={childOverrides}
                                        renderItem={({ step, override }) => (
                                            <List.Item
                                                key={step.stepConfigId}
                                                style={{ padding: 0, borderBottom: `1px solid ${antGrey.grey5}` }}
                                            >
                                                <NotificationStepConfigOverrideRow
                                                    appId={appId}
                                                    orgId={orgId}
                                                    workflowId={workflowId}
                                                    notificationId={notification.id as AppNotificationType}
                                                    stepGraphStep={step}
                                                    override={override}
                                                    destinations={destinationsByStepConfigId[step.stepConfigId] ?? []}
                                                    enabled={
                                                        optimistic[`${notification.id}#${step.stepConfigId}`] ??
                                                        override.enabled
                                                    }
                                                    isUpdating={parentIsUpdating}
                                                    platform={platform}
                                                    appStoreType={appStoreType}
                                                    availableChannelsById={availableChannelsById}
                                                    onClickSettings={() => {
                                                        setStepOverrideModal({
                                                            notificationId: notification.id as AppNotificationType,
                                                            stepConfigId: step.stepConfigId,
                                                            stepConfigName: step.stepName,
                                                            override,
                                                        });
                                                    }}
                                                    onToggleEnabled={(enabled) =>
                                                        handleToggle(notification, override, enabled)
                                                    }
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </Collapse.Panel>
                            </Collapse>
                        </List.Item>
                    );
                }}
            ></List>
            <Text>
                You can enable personal email notifications for any app-level notification from{' '}
                <Link to={createUserSettingsRoute(UserSettingsPageView.Email)}>
                    User settings &gt; Email notifications
                </Link>
                .
            </Text>
            <Text>
                Certain notifications also support email notifications to any address you specify in their respective
                notification settings.
            </Text>
            {notificationIdSelectedForSettings && (
                <NotificationSettingsModal
                    appId={props.appId}
                    orgId={props.orgId}
                    onClose={() => setShowNotificationSettings(false)}
                    open={showNotificationSettings}
                    notificationId={notificationIdSelectedForSettings}
                    onAfterClose={() => {
                        setNotificationIdSelectedForSettings(null);
                    }}
                />
            )}
            {stepOverrideModal && workflowId && (
                <NotificationSettingsModal
                    appId={appId}
                    orgId={orgId}
                    workflowId={workflowId}
                    notificationId={stepOverrideModal.notificationId}
                    stepConfigId={stepOverrideModal.stepConfigId}
                    stepConfigName={stepOverrideModal.stepConfigName}
                    override={stepOverrideModal.override}
                    open={true}
                    onClose={() => setStepOverrideModal(null)}
                    onAfterClose={() => setStepOverrideModal(null)}
                />
            )}
            <AddStepOverrideModal
                open={addOverrideForNotificationId != null}
                notification={
                    addOverrideForNotificationId ?
                        (notifications.find((n) => n.id === addOverrideForNotificationId) ?? null)
                    :   null
                }
                stepGraphStepsMap={stepGraphStepsMap}
                platform={platform}
                appStoreType={appStoreType}
                onClose={() => setAddOverrideForNotificationId(null)}
                onSelect={(step) => {
                    const notificationId = addOverrideForNotificationId;
                    setAddOverrideForNotificationId(null);
                    if (notificationId) {
                        setStepOverrideModal({
                            notificationId,
                            stepConfigId: step.stepConfigId,
                            stepConfigName: step.stepName,
                            override: null,
                        });
                    }
                }}
            />
        </div>
    );
};

export default AppNotifications;
